import { motion } from 'motion/react';
import { Shield, Anchor, Package, Compass, Play, Crosshair, Box, Layers, Zap } from 'lucide-react';
import { useBrand } from '../context/BrandContext';

export default function AnatomyDetailed() {
  const { assets, content, resolveMediaUrl } = useBrand();

  let features = [
    {
      id: "heat-fused",
      title: "ADVANCED HULL TECHNOLOGY",
      description: "Woven Drop-Stitch Cores, Fusion Lamination, and Heat-Welded Rails mechanically and thermally fuse multiple layers of industrial-grade PVC directly to the core substrate, eliminating heavy, error-prone manual glue layers.",
      icon: "Shield",
    },
    {
      id: "displacement-hull",
      title: "V-HULL GEOMETRY",
      description: "Proprietary V-Hull nose cuts through chop without plowing. Acts as a keel for laser-straight tracking in side winds.",
      icon: "Anchor",
    },
    {
      id: "dual-chamber-v2",
      title: "REDUNDANT AIRLIFT",
      description: "Two fully independent air chambers. Inner spine eliminates taco-flex. Safety redundancy stays buoyant if outer is compromised.",
      icon: "Shield",
    },
    {
      id: "nav-white",
      title: "THERMAL INTEGRITY",
      description: "Navigation White deck reflects UV rays, keeping internal air pressure stable across extreme temperature shifts.",
      icon: "Zap",
    }
  ];

  try {
    if (content.vesselBlueprint) {
      features = JSON.parse(content.vesselBlueprint);
    }
  } catch (e) {
    console.error("Failed to parse vesselBlueprint JSON");
  }

  const IconMap: Record<string, any> = {
    Shield, Anchor, Package, Compass, Play
  };

  return (<section className="py-32 bg-[#F2F2F0] relative overflow-hidden text-[#141414]" id="anatomy-detailed">
    {/* Blueprint Grid */}
    <div className="absolute inset-0 opacity-[0.2] pointer-events-none"
      style={{ backgroundImage: 'linear-gradient(#141414 1px, transparent 1px), linear-gradient(90deg, #141414 1px, transparent 1px)', backgroundSize: '100px 100px' }}
    />

    {/* Corner Gloom/Light */}
    <div className="absolute -bottom-[200px] -right-[200px] w-[600px] h-[600px] bg-ows-teal/5 rounded-full blur-[150px] pointer-events-none" />

    <div className="max-w-7xl mx-auto px-4 relative">
      {/* Section Header */}
      <div className="flex flex-col md:flex-row justify-between items-end mb-24 border-b border-[#141414]/10 pb-12 gap-8">
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Crosshair className="h-4 w-4 text-ows-teal" />
            <span className="text-[10px] font-mono font-bold uppercase tracking-[0.5em] text-ows-teal">ARCHIVE: OWS EXPEDITION CX2-BLUEPRINT</span>
          </div>
          <h2 className="text-4xl md:text-6xl font-display font-light uppercase tracking-tighter leading-none text-[#141414]">
            STRUCTURAL <br />
            <span className="text-[#141414]/20">INTEGRITY</span>
          </h2>
        </div>
        <div className="max-w-xs text-right">
          <p className="text-[10px] font-mono uppercase tracking-widest text-[#141414]/40 leading-relaxed font-bold">
            Engineering report on dual-chamber displacement architecture and hull redundancy.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
        {/* Central Media Asset */}
        <div className="lg:col-span-7 space-y-12">
          <div className="relative group p-12 bg-white border border-[#141414]/10 rounded-[4rem] aspect-[4/5] md:aspect-square flex items-center justify-center shadow-inner overflow-hidden">
            {/* Technical Annotations */}
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute top-12 left-12 w-32 border-t border-[#141414]/10" />
              <div className="absolute top-12 left-12 h-32 border-l border-[#141414]/10" />
              <div className="absolute bottom-12 right-12 w-32 border-b border-[#141414]/10 text-right">
                <span className="text-[8px] font-mono font-bold uppercase tracking-widest opacity-20 block mt-4 text-[#141414]">Hull Dimension {content.vesselDimensions || '14\' x 28"'}</span>
              </div>
            </div>

            <motion.img
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1.2, ease: "circOut" }}
              src={resolveMediaUrl(assets.boardImage)}
              alt="Technical Drawing"
              className="h-full w-auto object-contain brightness-100 drop-shadow-[0_20px_50px_rgba(0,0,0,0.1)] relative z-10 transition-transform duration-1000 group-hover:scale-105"
              referrerPolicy="no-referrer"
            />

            {/* Redundancy Marker */}
            <div className="absolute top-1/2 left-8 -translate-y-1/2 flex flex-col items-center gap-4">
              <div className="h-40 w-px bg-ows-teal" />
              <span className="text-[8px] font-mono font-bold uppercase tracking-[0.5em] text-ows-teal [writing-mode:vertical-rl] rotate-180">Safety Redundancy Zone</span>
              <div className="h-12 w-px bg-ows-teal" />
            </div>
          </div>

          {/* Sub-Technical Details */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="p-6 bg-white border border-[#141414]/10 rounded-3xl">
              <Layers className="h-4 w-4 text-ows-teal mb-3" />
              <span className="block text-[8px] font-mono font-bold uppercase tracking-widest opacity-30 mb-2 text-[#141414]">Structure</span>
              <span className="block text-xs font-bold uppercase tracking-widest text-[#141414]">Woven Drop-Stitch</span>
            </div>
            {/*
            // <div className="p-6 bg-white border border-[#141414]/10 rounded-3xl">
            //   <Box className="h-4 w-4 text-ows-orange mb-3" />
            //   <span className="block text-[8px] font-mono font-bold uppercase tracking-widest opacity-30 mb-2 text-[#141414]">Volume</span>
            //   <span className="block text-xs font-bold uppercase tracking-widest text-[#141414]">350L DISPLACED</span>
            // </div>
            // <div className="p-6 bg-white border border-[#141414]/10 rounded-3xl">
            //   <Zap className="h-4 w-4 text-ows-teal mb-3" />
            //   <span className="block text-[8px] font-mono font-bold uppercase tracking-widest opacity-30 mb-2 text-[#141414]">Response</span>
            //   <span className="block text-xs font-bold uppercase tracking-widest text-[#141414]">ULTRA-HULL</span>
            // </div>
            // <div className="p-6 bg-white border border-[#141414]/10 rounded-3xl">
            //   <Layers className="h-4 w-4 text-ows-teal mb-3" />
            //   <span className="block text-[8px] font-mono font-bold uppercase tracking-widest opacity-30 mb-2 text-[#141414]">Core Substrate</span>
            //   <span className="block text-xs font-bold uppercase tracking-widest text-[#141414]">Woven Drop-Stitch</span>
            // </div> */}
            <div className="p-6 bg-white border border-[#141414]/10 rounded-3xl">
              <Anchor className="h-4 w-4 text-ows-teal mb-3" />
              <span className="block text-[8px] font-mono font-bold uppercase tracking-widest opacity-30 mb-2 text-[#141414]">Hull Bond</span>
              <span className="block text-xs font-bold uppercase tracking-widest text-[#141414]">Fusion Lamination</span>
            </div>
            <div className="p-6 bg-white border border-[#141414]/10 rounded-3xl">
              <Zap className="h-4 w-4 text-ows-teal mb-3" />
              <span className="block text-[8px] font-mono font-bold uppercase tracking-widest opacity-30 mb-2 text-[#141414]">Seam Integrity</span>
              <span className="block text-xs font-bold uppercase tracking-widest text-[#141414]">Heat-Welded Rails</span>
            </div>
          </div>
        </div>

        {/* Feature Specs */}
        <div className="lg:col-span-5 space-y-4">
          <p className="text-[10px] font-mono font-bold uppercase tracking-[0.4em] text-[#141414]/30 px-4 mb-2">System Components</p>
          {features.map((feature, idx) => {
            const IconComp = IconMap[feature.icon] || Compass;
            return (
              <motion.div
                key={feature.id}
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1, duration: 0.5 }}
                className="bg-white p-8 rounded-3xl border border-[#141414]/5 hover:border-ows-teal/30 transition-all group"
              >
                <div className="flex items-start gap-5">
                  <div className="w-10 h-10 rounded-xl bg-[#141414]/5 flex items-center justify-center shrink-0 group-hover:bg-ows-teal transition-colors">
                    <IconComp className="h-5 w-5 text-[#141414]/30 group-hover:text-white" />
                  </div>
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-[8px] font-mono text-ows-teal font-bold bg-ows-teal/10 px-1.5 py-0.5 rounded uppercase">0{idx + 1}</span>
                      <h3 className="text-xs font-bold uppercase tracking-widest text-[#141414]">{feature.title}</h3>
                    </div>
                    <p className="text-[11px] font-mono text-[#141414]/60 uppercase leading-relaxed tracking-tighter group-hover:text-[#141414] transition-colors">
                      {feature.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            );
          })}

          {/* Archive Callout */}
          <div className="mt-12 p-10 bg-ows-teal text-white rounded-[3rem] relative overflow-hidden group shadow-2xl">
            <div className="absolute top-0 right-0 p-8 opacity-10 group-hover:opacity-20 transition-opacity">
              <Box className="h-24 w-24 text-white" />
            </div>
            <div className="relative z-10 space-y-4">
              <div className="flex items-center gap-3 text-white">
                <Shield className="h-5 w-5" />
                <span className="text-[9px] font-mono font-bold uppercase tracking-[0.4em]">Survival Certification</span>
              </div>
              <p className="text-sm font-serif italic text-white/95">
                "Every OWS EXPEDITION CX2 is built to handle loads well beyond standard deployment. This isn't recreation; it's a utility for the uncharted."
              </p>
              <div className="pt-4 flex items-center gap-4 border-t border-white/20">
                <span className="text-[9px] font-mono font-bold uppercase tracking-widest text-white font-bold opacity-80">VERIFIED BATCH 01 SERIES</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
  );
}
